import { useQuery } from '@tanstack/react-query'

import { Loader } from '@/components/Loader'

import { message, pGet, type AuditEvent } from './api'

/**
 * What platform administrators have done.
 *
 * Its own table, with no foreign keys: `audit_logs` hangs off an organization
 * and cascades with it, so the row recording "who deleted Globex" would be
 * deleted by the act it records. The organization's name is copied in as text
 * for the same reason, which is why a deleted carrier still has a name here.
 */

const WORDING: Record<string, string> = {
  'platform.signed_in': 'Signed in',
  'platform.password_changed': 'Changed their own password',
  'platform.admin_created': 'Created a platform administrator',
  'platform.admin_deleted': 'Deleted a platform administrator',
  'platform.admin_activated': 'Reactivated a platform administrator',
  'platform.admin_deactivated': 'Deactivated a platform administrator',
  'platform.admin_password_reset': 'Set a platform administrator’s password',
  'platform.organization_created': 'Created',
  'platform.organization_updated': 'Edited',
  'platform.organization_activated': 'Switched on',
  'platform.organization_deactivated': 'Switched off',
  'platform.organization_deleted': 'Deleted',
  'platform.organization_admin_added': 'Added an administrator to',
  'platform.organization_admin_activated': 'Reactivated an administrator of',
  'platform.organization_admin_deactivated': 'Deactivated an administrator of',
  'platform.organization_admin_password_reset': 'Set an administrator’s password at',
}

function describe(e: AuditEvent): string {
  const verb = WORDING[e.action] ?? e.action.replace(/^platform\./, '').replace(/_/g, ' ')
  return e.organizationName ? `${verb} ${e.organizationName}` : verb
}

function detailOf(e: AuditEvent): string {
  if (!e.payload) return ''
  return Object.entries(e.payload)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ')
}

export default function AuditPage() {
  const log = useQuery({
    queryKey: ['pf', 'audit'],
    queryFn: () => pGet<{ events: AuditEvent[] }>('/audit', { limit: 200 }),
  })

  const events = log.data?.events ?? []

  return (
    <>
      <div className="pf-head">
        <div>
          <h1 className="pf-head__h">Activity</h1>
          <p className="pf-head__d">Everything done from this portal, newest first.</p>
        </div>
      </div>

      {log.isError ? (
        <div className="ix-alert ix-alert--error" role="alert" style={{ marginBottom: 14 }}>
          <div>{message(log.error, 'The activity could not be loaded.')}</div>
        </div>
      ) : null}

      <div className="pf-card">
        <div className="pf-card__scroll">
          <table className="ix-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Detail</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {log.isLoading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="pf-loading">
                      <Loader size="md" label="Loading activity" />
                    </div>
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="pf-empty">
                    <span className="pf-empty__t">Nothing yet</span>
                    Creating a carrier, switching one off or adding an administrator is recorded here.
                  </td>
                </tr>
              ) : (
                events.map((e) => (
                  <tr key={e.id}>
                    <td className="pf-nowrap">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="pf-nowrap">
                      <span className="st-user__n">{e.actorName || e.actorEmail}</span>
                    </td>
                    <td>{describe(e)}</td>
                    {/* The whole payload is the tooltip: this is one line in a
                        column, and a wrapped JSON object would set the row
                        height for every row above and below it. */}
                    <td className="pf-detail" title={detailOf(e) || undefined}>
                      {detailOf(e) || <span className="st-muted">—</span>}
                    </td>
                    <td className="pf-nowrap">{e.ip || <span className="st-muted">—</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
